import { AnimatePresence, motion } from 'framer-motion';
import { Users, Clock, X } from 'lucide-react';
import { useTheme } from '../../../../../../context/ThemeContext.jsx';

export const ProductInfoModal = ({ showProductInfo, setShowProductInfo, product, companyState, stripHtml }) => {
  const { activeMode } = useTheme() || {};
  if (!showProductInfo) return null;

  const isDarkMode = activeMode === 'dark';
  const subtleSurface = isDarkMode ? 'var(--flex-surface-soft)' : '#f1f5f9';
  const softSurface = isDarkMode ? 'var(--flex-surface)' : '#f8fafc';
  const borderColor = isDarkMode ? 'var(--flex-border)' : '#e2e8f0';
  const primarySoftSurface = isDarkMode ? 'var(--flex-primary-soft)' : 'var(--flex-primary-soft, #eff6ff)';
  const primaryBorderColor = isDarkMode ? 'var(--flex-primary-muted)' : 'var(--flex-primary-muted, #bfdbfe)';

  const currencySymbol = companyState?.operatingLocation?.code === 'US' ? '$' : '₹';
  const price = parseFloat(product.price?.priceAmt || '0').toFixed(2);
  const imageUrl =
    product?.imageContents?.[0]?.displayLink ||
    product?.category?.imageContent?.displayLink ||
    null;

  const description = stripHtml(product.description) ||
    'This premium workspace offers a professional environment perfect for meetings, collaboration, and focused work. Equipped with modern amenities and comfortable seating.';

  return (
    <AnimatePresence>
      {showProductInfo && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.18 }}
          className="fixed inset-0 z-50 flex items-center justify-center p-4"
          style={{ backdropFilter: 'blur(10px)', background: 'rgba(2, 8, 23, 0.6)' }}
          onClick={() => setShowProductInfo(false)}
        >
          <motion.div
            initial={{ opacity: 0, scale: 0.94, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.96, y: 10 }}
            transition={{ duration: 0.24, ease: [0.16, 1, 0.3, 1] }}
            onClick={(e) => e.stopPropagation()}
            className="w-full"
            style={{ maxWidth: '560px' }}
          >
            <div
              className="bg-white w-full overflow-hidden"
              style={{
                borderRadius: '24px',
                boxShadow: '0 0 0 1px rgba(0,0,0,0.06), 0 24px 80px -12px rgba(0,0,0,0.45)',
                maxHeight: '90vh',
                overflowY: 'auto',
              }}
            >
              {/* ── Image ── */}
              <div className="relative w-full" style={{ height: '260px', flexShrink: 0 }}>
                {imageUrl ? (
                  <img
                    src={imageUrl}
                    alt={product.name || 'Space'}
                    className="w-full h-full object-cover"
                    onError={(e) => { e.target.onerror = null; e.target.style.display = 'none'; }}
                  />
                ) : (
                  <div
                    className="w-full h-full"
                    style={{ background: 'linear-gradient(135deg, var(--flex-primary-soft, #e0e7ff) 0%, var(--flex-accent-soft, #c7d2fe) 100%)' }}
                  />
                )}

                {/* Close */}
                <button
                  onClick={() => setShowProductInfo(false)}
                  className="absolute top-4 right-4 w-9 h-9 rounded-full flex items-center justify-center"
                  style={{ background: 'rgba(0,0,0,0.45)', backdropFilter: 'blur(6px)' }}
                >
                  <X className="w-4 h-4 text-white" strokeWidth={2.5} />
                </button>

                {/* Category pill */}
                {product.category?.name && (
                  <div
                    className="absolute bottom-4 left-4 px-3 py-1.5 rounded-full text-xs font-semibold text-white"
                    style={{ background: 'rgba(0,0,0,0.45)', backdropFilter: 'blur(6px)' }}
                  >
                    {product.category.name}
                  </div>
                )}
              </div>

              {/* ── Content ── */}
              <div className="p-6">

                {/* Name + price row */}
                <div className="flex items-start justify-between gap-4 mb-4">
                  <div>
                    <h3
                      className="font-bold text-slate-900 leading-snug"
                      style={{ fontSize: '22px', letterSpacing: '-0.4px' }}
                    >
                      {product.name || 'Premium Workspace'}
                    </h3>
                  </div>
                  <div className="flex items-baseline gap-0.5 flex-shrink-0">
                    <span className="text-slate-400 font-semibold" style={{ fontSize: '14px' }}>
                      {currencySymbol}
                    </span>
                    <span
                      className="text-slate-900 font-black leading-none"
                      style={{ fontSize: '30px', letterSpacing: '-0.5px' }}
                    >
                      {price}
                    </span>
                    <span className="text-slate-400 font-medium ml-0.5" style={{ fontSize: '13px' }}>
                      /hr
                    </span>
                  </div>
                </div>

                {/* Badges */}
                <div className="flex items-center gap-2 mb-5">
                  {product.capacity && (
                    <div
                      className="flex items-center gap-1.5 px-3 py-1.5 rounded-full"
                      style={{ background: subtleSurface, border: `1px solid ${borderColor}` }}
                    >
                      <Users className="w-3.5 h-3.5 text-slate-400" strokeWidth={2} />
                      <span className="text-slate-600 font-semibold" style={{ fontSize: '12px' }}>
                        Up to {product.capacity} {product.capacity === 1 ? 'person' : 'people'}
                      </span>
                    </div>
                  )}
                  <div
                    className="flex items-center gap-1.5 px-3 py-1.5 rounded-full"
                    style={{ background: primarySoftSurface, border: `1px solid ${primaryBorderColor}` }}
                  >
                    <Clock className="w-3.5 h-3.5 text-blue-400" strokeWidth={2} />
                    <span className="text-blue-600 font-semibold" style={{ fontSize: '12px' }}>
                      1 hour minimum
                    </span>
                  </div>
                </div>

                {/* Divider */}
                <div style={{ height: '1px', background: borderColor, marginBottom: '16px' }} />

                {/* Description — full, no clamp */}
                <div className="mb-5">
                  <p
                    className="text-xs font-bold text-slate-400 uppercase tracking-widest mb-2"
                  >
                    About this space
                  </p>
                  <p
                    className="text-slate-600 leading-relaxed"
                    style={{ fontSize: '14px' }}
                  >
                    {description}
                  </p>
                </div>

                {/* Amenities */}
                {product.amenities?.length > 0 && (
                  <>
                    <div style={{ height: '1px', background: borderColor, marginBottom: '16px' }} />
                    <div className="mb-5">
                      <p className="text-xs font-bold text-slate-400 uppercase tracking-widest mb-3">
                        Amenities
                      </p>
                      <div className="flex flex-wrap gap-2">
                        {product.amenities.map((amenity, idx) => (
                          <span
                            key={idx}
                            className="text-slate-500 font-medium"
                            style={{
                              fontSize: '12px',
                              background: softSurface,
                              border: `1px solid ${borderColor}`,
                              padding: '4px 12px',
                              borderRadius: '100px',
                            }}
                          >
                            {amenity}
                          </span>
                        ))}
                      </div>
                    </div>
                  </>
                )}

                {/* CTA */}
                <button
                  onClick={() => setShowProductInfo(false)}
                  className="w-full font-bold text-white transition-all active:scale-[0.98] hover:opacity-90"
                  style={{
                    height: '52px',
                    borderRadius: '14px',
                    fontSize: '15px',
                    background: 'linear-gradient(135deg, var(--flex-primary, #2563eb) 0%, var(--flex-primary-hover, #1d4ed8) 100%)',
                    boxShadow: '0 4px 16px var(--flex-primary-muted, rgba(37,99,235,0.4))',
                    letterSpacing: '0.01em',
                  }}
                >
                  Got it
                </button>
              </div>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};
